import { ArrowRight, ListChecks, SlidersHorizontal } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  guidedStageLabels,
  guidedStages,
} from '@/features/guided-builder/guidedBuilderStages'
import type { CreateChatModeModalProps } from './CreateChatModeModal.types'

const journeyStages = guidedStages.map((stage) => guidedStageLabels[stage])

export function CreateChatModeModal({
  open,
  onOpenChange,
  onGuidedSetup,
  onFullBuilder,
}: CreateChatModeModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="overflow-hidden p-0">
        <DialogHeader className="border-b border-border">
          <DialogTitle>How do you want to build?</DialogTitle>
          <DialogDescription>
            Follow a guided setup or start directly in the full builder.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 p-6 sm:grid-cols-2">
          <button
            type="button"
            onClick={onGuidedSetup}
            className="group flex min-h-64 flex-col rounded-2xl border border-primary/30 bg-primary/[0.04] p-5 text-left transition-[border-color,background-color,box-shadow] hover:border-primary/60 hover:bg-primary/[0.07] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <span className="mb-5 flex size-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <ListChecks aria-hidden="true" className="size-5" />
            </span>

            <span className="text-base font-semibold text-foreground">
              Guided Setup
            </span>
            <span className="mt-1 text-sm leading-5 text-muted-foreground">
              Build a complete chat one section at a time, with guidance and a
              live preview.
            </span>

            <span
              className="mt-5 flex gap-1"
              aria-label={journeyStages.join(', ')}
            >
              {journeyStages.map((stage, index) => (
                <span
                  key={stage}
                  className={
                    index === 0
                      ? 'h-1.5 flex-1 rounded-full bg-primary'
                      : 'h-1.5 flex-1 rounded-full bg-primary/20'
                  }
                  title={stage}
                />
              ))}
            </span>

            <span className="mt-auto flex items-center gap-2 pt-5 text-sm font-medium text-primary">
              Start guided setup
              <ArrowRight
                aria-hidden="true"
                className="size-4 transition-transform group-hover:translate-x-0.5"
              />
            </span>
          </button>

          <button
            type="button"
            onClick={onFullBuilder}
            className="group flex min-h-64 flex-col rounded-2xl border border-border bg-background p-5 text-left transition-[border-color,background-color,box-shadow] hover:border-foreground/20 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <span className="mb-5 flex size-10 items-center justify-center rounded-full bg-muted text-foreground">
              <SlidersHorizontal aria-hidden="true" className="size-5" />
            </span>

            <span className="text-base font-semibold text-foreground">
              Full Builder
            </span>
            <span className="mt-1 text-sm leading-5 text-muted-foreground">
              Start with every configuration section available at once.
            </span>

            <span className="mt-auto flex items-center gap-2 pt-5 text-sm font-medium text-foreground">
              Open full builder
              <ArrowRight
                aria-hidden="true"
                className="size-4 transition-transform group-hover:translate-x-0.5"
              />
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
